import {
  CAlert,
  CButton,
  CCloseButton,
  CCol,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CDatePicker,
  CForm,
  CFormFeedback,
  CFormInput,
  CFormLabel,
  CLoadingButton,
  CTimePicker,
} from '@coreui/react-pro'
import moment from 'moment'
import React, { useRef, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import RoundService from 'src/service/RoundService'
import ToastComponent from 'src/components/common/TaostComponent'

const AddForm = ({ visible, onClose, roundNumber, fetchUsers }) => {
  const [loader, setLoader] = useState(false)
  const [errorMessages, setErrorMessages] = useState([])
  const keepOpenRef = useRef(false)

  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [lockoutStartTime, setLockoutStartTime] = useState(null)
  const [lockoutEndTime, setLockoutEndTime] = useState(null)

  const validationSchema = Yup.object().shape({
    round: Yup.number().required('Round is required'),
    start_date: Yup.date().required('Start Date is required'),
    end_date: Yup.date().required('End Date is required'),
    start_time: Yup.string().required('Start Time is required'),
    end_time: Yup.string().required('End Time is required'),
  })

  const formik = useFormik({
    initialValues: {
      round: roundNumber,
      start_date: '',
      end_date: '',
      start_time: '',
      end_time: '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (data, actions) => {
      const payload = {
        ...data,
        start_date: moment(startDate).format('YYYY-MM-DD'),
        end_date: moment(endDate).format('YYYY-MM-DD'),
        start_time: lockoutStartTime,
        end_time: lockoutEndTime,
      }
      setLoader(true)
      RoundService.saveRound(payload)
        .then((res) => {
          if (res.status === 200) {
            setErrorMessages([])
            fetchUsers()
            ToastComponent(res.message, 'success')
            resetFields(actions)
            if (!keepOpenRef.current) {
              onClose()
            }
          } else {
            const errors =
              res.errors && Array.isArray(res.errors)
                ? res.errors
                : [res.message || 'Something went wrong.']
            setErrorMessages(errors)
          }
          setLoader(false)
        })
        .catch(() => {
          ToastComponent('Something went wrong. Please try again.', 'error')
          setLoader(false)
        })
    },
  })

  const resetFields = (actions) => {
    actions.resetForm({
      values: { round: '', start_date: '', end_date: '', start_time: '', end_time: '' },
    })
    setStartDate(null)
    setEndDate(null)
    setLockoutStartTime(null)
    setLockoutEndTime(null)
  }

  const handleClose = () => {
    resetFields(formik)
    setErrorMessages([])
    onClose()
  }

  const submitForm = (keepOpen) => {
    keepOpenRef.current = keepOpen
    formik.handleSubmit()
  }

  const handleStartDate = (date) => {
    setStartDate(date)
    setEndDate(null)
    formik.setFieldValue('start_date', moment(date).format('YYYY-MM-DD'))
  }

  const handleEndDate = (date) => {
    setEndDate(date)
    formik.setFieldValue('end_date', moment(date).format('YYYY-MM-DD'))
  }

  const handleLockoutStartTime = (event) => {
    const formatted = moment(event, 'HH:mm').format('HH:mm')
    setLockoutStartTime(formatted)
    formik.setTouched({ ...formik.touched, start_time: true })
    formik.setFieldValue('start_time', formatted)
  }

  const handleLockoutEndTime = (event) => {
    const formatted = moment(event, ['HH:mm']).format('HH:mm')
    setLockoutEndTime(formatted)
    formik.setTouched({ ...formik.touched, end_time: true })
    formik.setFieldValue('end_time', formatted)
  }

  return (
    <COffcanvas
      placement="end"
      visible={visible}
      onHide={handleClose}
      backdrop
      style={{ width: 480 }}
    >
      <COffcanvasHeader>
        <div>
          <COffcanvasTitle>Add Round</COffcanvasTitle>
          <div className="text-medium-emphasis small">
            Create round {roundNumber}, its lockout window and trade settings.
          </div>
        </div>
        <CCloseButton className="text-reset" onClick={handleClose} />
      </COffcanvasHeader>
      <COffcanvasBody className="d-flex flex-column">
        {errorMessages.length > 0 && (
          <div className="error-list">
            {errorMessages.map((error, index) => (
              <CAlert color="danger" key={index}>
                {error}
              </CAlert>
            ))}
          </div>
        )}
        <CForm className="row g-3 flex-grow-1" onSubmit={formik.handleSubmit}>
          <CCol xs={12}>
            <CFormLabel htmlFor="round">Round</CFormLabel>
            <CFormInput
              placeholder="Round"
              value={roundNumber ?? ''}
              aria-label="round"
              id="round"
              name="round"
              disabled
            />
            {formik.errors.round && formik.touched.round && (
              <CFormFeedback invalid className="d-block">
                {formik.errors.round}
              </CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="start_date">Round Start Date *</CFormLabel>
            <CDatePicker date={startDate} onDateChange={handleStartDate} />
            {formik.errors.start_date && formik.touched.start_date && (
              <CFormFeedback invalid className="d-block">
                {formik.errors.start_date}
              </CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="start_time">Round Start Time *</CFormLabel>
            <CTimePicker
              locale="en-US"
              value={lockoutStartTime}
              seconds={false}
              className={formik.errors.start_time && formik.touched.start_time ? 'is-invalid' : ''}
              onTimeChange={handleLockoutStartTime}
            />
            {formik.errors.start_time && formik.touched.start_time && (
              <CFormFeedback invalid className="d-block">
                {formik.errors.start_time}
              </CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="end_date">Round End Date *</CFormLabel>
            <CDatePicker date={endDate} minDate={startDate} onDateChange={handleEndDate} />
            {formik.errors.end_date && formik.touched.end_date && (
              <CFormFeedback invalid className="d-block">
                {formik.errors.end_date}
              </CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="end_time">Round End Time *</CFormLabel>
            <CTimePicker
              locale="en-US"
              value={lockoutEndTime}
              seconds={false}
              className={formik.errors.end_time && formik.touched.end_time ? 'is-invalid' : ''}
              onTimeChange={handleLockoutEndTime}
            />
            {formik.errors.end_time && formik.touched.end_time && (
              <CFormFeedback invalid className="d-block">
                {formik.errors.end_time}
              </CFormFeedback>
            )}
          </CCol>
        </CForm>
      </COffcanvasBody>
      <div className="d-flex justify-content-between gap-2 p-3 border-top">
        <CButton color="secondary" variant="outline" onClick={handleClose}>
          Cancel
        </CButton>
        <div className="d-flex gap-2">
          <CLoadingButton
            color="secondary"
            variant="outline"
            loading={loader}
            onClick={() => submitForm(true)}
          >
            Save &amp; Add Another
          </CLoadingButton>
          <CLoadingButton color="primary" loading={loader} onClick={() => submitForm(false)}>
            Create Round
          </CLoadingButton>
        </div>
      </div>
    </COffcanvas>
  )
}

export default AddForm
